const PASSWORD_ENCRYPTION_PREFIX = "enc:rsa-oaep-sha256:";

export async function encodePasswordForSend(password: string): Promise<string> {
  const publicKey = getPublicKeyConfig();
  if (!publicKey) {
    if (import.meta.env.PROD) {
      throw new Error("Missing VITE_AUTH_PASSWORD_PUBLIC_KEY");
    }
    return password;
  }

  const key = await importPublicKey(publicKey);
  const cipherText = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    key,
    new TextEncoder().encode(password)
  );
  return `${PASSWORD_ENCRYPTION_PREFIX}${base64UrlEncode(new Uint8Array(cipherText))}`;
}

function getPublicKeyConfig(): JsonWebKey | null {
  const rawValue = import.meta.env.VITE_AUTH_PASSWORD_PUBLIC_KEY?.trim();
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as JsonWebKey;
    if (parsed.kty !== "RSA" || typeof parsed.n !== "string" || typeof parsed.e !== "string") {
      throw new Error("Public key must be an RSA JWK with n and e values");
    }
    return parsed;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid JSON";
    throw new Error(`Invalid VITE_AUTH_PASSWORD_PUBLIC_KEY: ${message}`);
  }
}

async function importPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "jwk",
    jwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}